import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { type Language, type TranslationKey, getTranslation } from './translations'

interface LanguageContextValue {
  lang: Language
  setLang: (lang: Language) => void
  /** Look up a translation by dot-key. Returns string or function depending on the key. */
  t: <K extends TranslationKey>(key: K) => ReturnType<typeof getTranslation<K>>
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>('he')

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang)
    document.documentElement.dir = newLang === 'he' ? 'rtl' : 'ltr'
    document.documentElement.lang = newLang
  }, [])

  const t = useCallback(
    <K extends TranslationKey>(key: K) => getTranslation(key, lang),
    [lang],
  )

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return ctx
}
